import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Heart, Eye, MessageCircle, Star, Crown, CreditCard } from 'lucide-react';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';

const PremiumPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Heart,
      title: 'Unlimited Likes',
      description: 'Like as many profiles as you want'
    },
    {
      icon: Eye,
      title: 'See Who Likes You',
      description: 'View all your admirers instantly'
    },
    {
      icon: MessageCircle,
      title: 'Priority Messages',
      description: 'Your messages appear first'
    },
    {
      icon: Star,
      title: '5 Super Likes Daily',
      description: 'Stand out with Super Likes'
    },
    {
      icon: Crown,
      title: 'Exclusive Badges',
      description: 'Show your premium status'
    }
  ];

  const plans = [
    {
      duration: 'Weekly',
      piPrice: 'π 5.99',
      usdPrice: '$5.99',
      savings: null,
      popular: false
    },
    {
      duration: 'Monthly',
      piPrice: 'π 19.99',
      usdPrice: '$19.99',
      savings: 'Save 20%',
      popular: true
    },
    {
      duration: 'Yearly',
      piPrice: 'π 199.99',
      usdPrice: '$199.99',
      savings: 'Save 50%',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Go Premium</h1>
      </div>

      <div className="px-4 pb-8">
        {/* Hero Section */}
        <Card className="p-6 text-center mb-6 bg-gradient-to-br from-purple-600 to-pink-500 text-white border-none">
          <Zap size={48} className="mx-auto mb-4 text-yellow-300" />
          <h2 className="text-2xl font-bold mb-2">Unlock Premium Features</h2>
          <p className="opacity-90">Get the most out of your dating experience</p>
        </Card>

        {/* Features */}
        <div className="space-y-3 mb-6">
          {features.map((feature, index) => (
            <Card key={index} className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <feature.icon size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100">{feature.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{feature.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Pricing Plans */}
        <div className="space-y-3 mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Choose Your Plan</h3>
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`p-4 border-2 ${
                plan.popular 
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30' 
                  : 'border-transparent'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">{plan.duration}</h3>
                    {plan.popular && (
                      <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-full">
                        Most Popular
                      </span>
                    )}
                  </div>
                  {plan.savings && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">{plan.savings}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="flex flex-col gap-1">
                    <p className="text-lg font-bold text-gray-800 dark:text-gray-100">{plan.piPrice}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{plan.usdPrice}</p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">per {plan.duration.toLowerCase()}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Payment Options */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Payment Options</h3>
          <div className="grid grid-cols-2 gap-3">
            <Card className="p-4 text-center">
              <div className="text-3xl mb-2">🥧</div>
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Pi Network</h4>
              <p className="text-xs text-gray-600 dark:text-gray-300">Pay with Pi cryptocurrency</p>
            </Card>
            <Card className="p-4 text-center">
              <div className="text-3xl mb-2">💳</div>
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Credit Card</h4>
              <p className="text-xs text-gray-600 dark:text-gray-300">Visa, Mastercard, Amex</p>
            </Card>
          </div>
        </div>

        {/* CTA Button */}
        <Button
          onClick={() => navigate('/payment')}
          variant="gradient"
          size="lg"
          className="w-full mb-4"
        >
          <Zap size={20} className="mr-2" />
          Continue with Premium
        </Button>

        {/* Payment Security */}
        <Card className="p-4 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 mb-4">
          <div className="flex items-center gap-3">
            <CreditCard size={20} className="text-green-600 dark:text-green-400" />
            <div>
              <h4 className="font-semibold text-green-800 dark:text-green-200">Secure Payments</h4>
              <p className="text-sm text-green-700 dark:text-green-300">
                All payments are encrypted and secure. Pi Network payments through official SDK, 
                credit card payments powered by Stripe with PCI DSS compliance.
              </p>
            </div>
          </div>
        </Card>

        {/* Terms */}
        <p className="text-xs text-white/70 text-center">
          Payment will be charged to your selected payment method. Subscription automatically renews unless cancelled.
          By purchasing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
};

export default PremiumPage;